import type { SelectOption } from '@shared/types'

// Canonical offering status / type values — mirror the admin-service
// enums (src/shared/enums/property.enums.ts). Use these constants instead
// of hardcoding 'draft' / 'primary' literals across the module.

export const OFFERING_STATUS = {
  DRAFT: 'draft',
  IN_VERIFICATION: 'in_verification',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
} as const

export type OfferingStatus = (typeof OFFERING_STATUS)[keyof typeof OFFERING_STATUS]

export const OFFERING_TYPE = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const

export type OfferingType = (typeof OFFERING_TYPE)[keyof typeof OFFERING_TYPE]

// Funding lifecycle — independent of the approval status above. Mirrors the
// admin-service OFFERING_PROCESSING_STATUS enum. A "Live" offering is one with
// processing status OPEN (actively accepting investor participation).
export const OFFERING_PROCESSING_STATUS = {
  PENDING: 'pending',
  OPEN: 'open',
  FILLED: 'filled',
  COMPLETED: 'completed',
} as const

export type OfferingProcessingStatus =
  (typeof OFFERING_PROCESSING_STATUS)[keyof typeof OFFERING_PROCESSING_STATUS]

// Terminal funding states shown together on the "Fully Funded & Completed" queue —
// the final stage of the offering lifecycle after Live. Mirrors the admin-service
// offeringProcessingStatusIn filter.
export const FULLY_FUNDED_PROCESSING_STATUSES: OfferingProcessingStatus[] = [
  OFFERING_PROCESSING_STATUS.FILLED,
  OFFERING_PROCESSING_STATUS.COMPLETED,
]

// VASP listing lifecycle statuses (mirror of the admin-service integrations/vasp
// ListingStatus enum). Used to filter the Rejected Offerings queue by VASP outcome —
// never hardcode 'rejected' against the vaspListingStatus filter.
export const VASP_LISTING_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  OPEN: 'open',
  FILLED: 'filled',
  AWAITING_REGISTRATION: 'awaiting_registration',
  AWAITING_APPROVAL: 'awaiting_approval',
  APPROVED: 'approved',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
} as const

export type VaspListingStatus = (typeof VASP_LISTING_STATUS)[keyof typeof VASP_LISTING_STATUS]

// Status → StatusBadge color. Mirrors PropertyList's PROPERTY_STATUS_COLOR map.
type StatusBadgeColor = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'

// VASP listing status → StatusBadge color. OCP lookup keyed by the raw string
// (coding-principles §9.4) so a status Ctrl-Alt adds later resolves to neutral
// rather than falling through an if-chain. Mirrors VASP_STATUS_COLOR_MAP in
// @shared/constants/vaspStatus.
export const VASP_LISTING_STATUS_COLOR: Record<string, StatusBadgeColor> = {
  [VASP_LISTING_STATUS.DRAFT]: 'neutral',
  [VASP_LISTING_STATUS.PENDING]: 'warning',
  [VASP_LISTING_STATUS.OPEN]: 'success',
  [VASP_LISTING_STATUS.FILLED]: 'info',
  [VASP_LISTING_STATUS.AWAITING_REGISTRATION]: 'warning',
  [VASP_LISTING_STATUS.AWAITING_APPROVAL]: 'warning',
  [VASP_LISTING_STATUS.APPROVED]: 'success',
  [VASP_LISTING_STATUS.COMPLETED]: 'primary',
  [VASP_LISTING_STATUS.REJECTED]: 'danger',
  [VASP_LISTING_STATUS.CLOSED]: 'neutral',
  [VASP_LISTING_STATUS.CANCELLED]: 'neutral',
}

export function vaspListingStatusColor(status: string | null | undefined): StatusBadgeColor {
  if (!status) return 'neutral'
  return VASP_LISTING_STATUS_COLOR[status] ?? 'neutral'
}

// Shown in place of a badge when an approved offering has never been sent to VASP.
export const VASP_LISTING_NOT_SENT_LABEL = 'Not sent'

export const OFFERING_STATUS_COLOR: Record<string, StatusBadgeColor> = {
  [OFFERING_STATUS.DRAFT]: 'warning',
  [OFFERING_STATUS.IN_VERIFICATION]: 'info',
  [OFFERING_STATUS.APPROVED]: 'success',
  [OFFERING_STATUS.REJECTED]: 'danger',
  [OFFERING_STATUS.CANCELLED]: 'neutral',
  [OFFERING_STATUS.COMPLETED]: 'primary',
}

export const OFFERING_PROCESSING_STATUS_COLOR: Record<string, StatusBadgeColor> = {
  [OFFERING_PROCESSING_STATUS.PENDING]: 'warning',
  [OFFERING_PROCESSING_STATUS.OPEN]: 'success',
  [OFFERING_PROCESSING_STATUS.FILLED]: 'info',
  [OFFERING_PROCESSING_STATUS.COMPLETED]: 'primary',
}

export const OFFERING_TYPE_OPTIONS = [
  { label: 'Primary', value: OFFERING_TYPE.PRIMARY },
  { label: 'Secondary', value: OFFERING_TYPE.SECONDARY },
]

// Sort options shown in the DataTableV2 header sort dropdown across every
// offerings table. The value encodes `<column>:<direction>`; the container
// splits it into the sortBy / sortOrder query params (see GetAllOfferingsParams).
export const OFFERING_SORT_OPTIONS: SelectOption[] = [
  { label: 'Newest first', value: 'created_at:DESC' },
  { label: 'Oldest first', value: 'created_at:ASC' },
  { label: 'Listing price (high → low)', value: 'listing_price:DESC' },
  { label: 'Listing price (low → high)', value: 'listing_price:ASC' },
  { label: 'Window start (soonest)', value: 'investment_window_start:ASC' },
]

export const DEFAULT_OFFERING_SORT = 'created_at:DESC'

// Canonical approval-status filter options for the all-offerings directory.
// Mirrors the OFFERING_STATUS enum — never hardcode 'open' / 'pending_review'
// / 'closed' (those stale values don't exist on the backend).
export const OFFERING_STATUS_OPTIONS = [
  { label: 'Draft', value: OFFERING_STATUS.DRAFT },
  { label: 'In Verification', value: OFFERING_STATUS.IN_VERIFICATION },
  { label: 'Approved', value: OFFERING_STATUS.APPROVED },
  { label: 'Rejected', value: OFFERING_STATUS.REJECTED },
  { label: 'Cancelled', value: OFFERING_STATUS.CANCELLED },
  { label: 'Completed', value: OFFERING_STATUS.COMPLETED },
]

// ── Funding status (admin view of what investors see) ─────────────────────────
// Admin does NOT show the investor-facing Live/Launching tabs; instead a single
// column reflects what the offering is currently showing as on the investor
// platform. Derived from the funding (processing) status plus both ends of the
// investment window. Returns null when the offering is not on the platform yet
// (still in approval / VASP onboarding).
//
// The window end is what makes this a derivation rather than a relabelling. The
// backend has no `expired` processing status — an offering that never filled
// just sits at `open` with a window end in the past, which reads as "Live" if
// you only look at the status. The operator needs to see that it lapsed, so the
// date decides.

export type OfferingPlatformLabel =
  | 'Live'
  | 'Launching Soon'
  | 'Fully Funded'
  | 'Completed'
  | 'Expired'

export interface OfferingPlatformDisplay {
  label: OfferingPlatformLabel
  color: StatusBadgeColor
}

/**
 * True once the investment window has closed. An absent or unparseable end date
 * never expires an offering — a missing date is unknown, not elapsed, and
 * marking a live offering expired off a bad string is the worse error.
 */
function isWindowClosed(windowEnd: string | null | undefined): boolean {
  if (!windowEnd) return false
  const endMs = new Date(windowEnd).getTime()
  return !Number.isNaN(endMs) && endMs < Date.now()
}

export function getOfferingPlatformDisplay(
  processingStatus: string | null | undefined,
  windowStart: string | null | undefined,
  windowEnd?: string | null,
): OfferingPlatformDisplay | null {
  switch (processingStatus) {
    // Checked before the window, and in this order: an offering that filled or
    // completed reached its goal, so its window closing afterwards is just the
    // calendar catching up. Only an unfilled one expires.
    case OFFERING_PROCESSING_STATUS.FILLED:
      return { label: 'Fully Funded', color: 'info' }
    case OFFERING_PROCESSING_STATUS.COMPLETED:
      return { label: 'Completed', color: 'primary' }
    case OFFERING_PROCESSING_STATUS.PENDING:
      // A raise that never opened and whose window has passed is as lapsed as
      // one that opened and failed to fill.
      return isWindowClosed(windowEnd)
        ? { label: 'Expired', color: 'danger' }
        : { label: 'Launching Soon', color: 'warning' }
    case OFFERING_PROCESSING_STATUS.OPEN: {
      if (isWindowClosed(windowEnd)) return { label: 'Expired', color: 'danger' }
      const notStarted = !!windowStart && new Date(windowStart).getTime() > Date.now()
      return notStarted
        ? { label: 'Launching Soon', color: 'warning' }
        : { label: 'Live', color: 'success' }
    }
    default:
      return null
  }
}
